import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import "./reporting.css";

function formatCurrency(value) {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(Number(value));
}

function formatNumber(value, digits = 1) {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    return Number(value).toLocaleString("en-US", {
        maximumFractionDigits: digits,
        minimumFractionDigits: 0,
    });
}

function formatPercent(value) {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    return `${formatNumber(value, 1)}%`;
}

function channelPillClass(channel) {
    const key = String(channel || "").toLowerCase();
    if (key.includes("ebay")) return "report-channel-pill is-ebay";
    if (key.includes("walmart")) return "report-channel-pill is-walmart";
    return "report-channel-pill";
}

export function ReportScroll({ children }) {
    return <div className="report-scroll">{children}</div>;
}

export function ReportToolbar({ children }) {
    return <div className="report-toolbar">{children}</div>;
}

export function ReportContent({ children }) {
    return <div className="report-content">{children}</div>;
}

export function ReportLoading({ label = "Loading report…" }) {
    return (
        <div className="report-loading">
            <CircularProgress size={26} sx={{ color: "#0d9488" }} />
            <span>{label}</span>
        </div>
    );
}

export function ReportError({ message = "Something went wrong loading this report." }) {
    return <div className="report-error">{message}</div>;
}

export function SpotlightStrip({ items }) {
    return (
        <div className="report-spotlight" role="group" aria-label="Key metrics">
            {items.map((item) => (
                <div key={item.label} className="report-spotlight-item">
                    <p className="report-spotlight-label">{item.label}</p>
                    <p className="report-spotlight-value">{item.value}</p>
                    {item.hint ? (
                        <p className="report-spotlight-hint">{item.hint}</p>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

export function ReportSection({ eyebrow, title, subtitle, action, children }) {
    return (
        <section className="report-section">
            <div className="report-section-header">
                <div className="report-section-copy">
                    {eyebrow ? <p className="report-section-eyebrow">{eyebrow}</p> : null}
                    <h2 className="report-section-title">{title}</h2>
                    {subtitle ? (
                        <p className="report-section-subtitle">{subtitle}</p>
                    ) : null}
                </div>
                {action || null}
            </div>
            {children}
        </section>
    );
}

export function KpiGrid({ children }) {
    return <div className="report-kpi-grid">{children}</div>;
}

export function KpiCard({ label, value, hint, tone }) {
    const valueClass =
        tone === "positive"
            ? "report-kpi-value is-positive"
            : tone === "negative"
              ? "report-kpi-value is-negative"
              : tone === "accent"
                ? "report-kpi-value is-accent"
                : "report-kpi-value";

    return (
        <div className="report-kpi">
            <p className="report-kpi-label">{label}</p>
            <p className={valueClass}>{value}</p>
            {hint ? <p className="report-kpi-hint">{hint}</p> : null}
        </div>
    );
}

export function BreakdownPanel({ title, children }) {
    return (
        <div className="report-panel">
            {title ? (
                <div className="report-panel-header">
                    <h3 className="report-panel-title">{title}</h3>
                </div>
            ) : null}
            <div className="report-panel-body">{children}</div>
        </div>
    );
}

export function ChannelTable({ rows, columns, emptyLabel = "No breakdown available yet." }) {
    if (!rows?.length) {
        return <p className="report-empty">{emptyLabel}</p>;
    }

    return (
        <div className="table-responsive">
            <table className="report-table">
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                scope="col"
                                style={{ textAlign: col.align || "left" }}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key || row.channel}>
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    style={{ textAlign: col.align || "left" }}
                                    className={col.muted ? "is-muted" : col.primary ? "is-primary" : undefined}
                                >
                                    {col.render ? col.render(row) : row[col.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function ChannelPill({ channel }) {
    return <span className={channelPillClass(channel)}>{channel || "Unknown"}</span>;
}

const AGING_TONE = {
    "<30d": "fresh",
    "30-60d": "aging",
    "60-90d": "risk",
    "90d+": "stale",
};

export function AgingDistribution({ buckets }) {
    const list = Array.isArray(buckets) ? buckets : [];
    const totalValue = list.reduce((sum, bucket) => sum + (Number(bucket.value) || 0), 0);

    if (!list.length) {
        return <p className="report-empty">No on-hand inventory with aging data.</p>;
    }

    return (
        <div className="report-aging">
            <div className="report-aging-track" aria-hidden="true">
                {list.map((bucket) => {
                    const value = Number(bucket.value) || 0;
                    const width = totalValue > 0 ? (value / totalValue) * 100 : 0;
                    if (width <= 0) return null;
                    return (
                        <div
                            key={bucket.key}
                            className={`report-aging-segment is-${AGING_TONE[bucket.key] || "fresh"}`}
                            style={{ width: `${width}%` }}
                            title={`${bucket.label}: ${formatCurrency(value)}`}
                        />
                    );
                })}
            </div>
            <div className="report-aging-legend">
                {list.map((bucket) => (
                    <div key={bucket.key} className="report-aging-legend-item">
                        <div className="report-aging-legend-top">
                            <span
                                className={`report-aging-dot is-${AGING_TONE[bucket.key] || "fresh"}`}
                            />
                            <span className="report-aging-legend-label">{bucket.label}</span>
                        </div>
                        <p className="report-aging-legend-value">
                            {formatCurrency(bucket.value)}
                        </p>
                        <p className="report-aging-legend-units">
                            {formatNumber(bucket.units, 0)} units
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ReportNote({ children }) {
    return <p className="report-note">{children}</p>;
}

export function ReportSplit({ children }) {
    return <div className="report-split">{children}</div>;
}

export { formatCurrency, formatNumber, formatPercent };
